import {Injectable} from '@angular/core';
import {AlertModalService} from '../../../common/component/modal';
import {SpatialUtilService} from '../spatial-util.service';
import {SpatialSequenceService} from '../../../common/service/spatial-sequence.service';
import {FeaturesApplyInsertService} from '../features-apply-insert.service';
import {FeatureApplyUpdateService} from '../feature-apply-update.service';
import {tap} from 'rxjs';

/* eslint-disable @typescript-eslint/no-explicit-any */
@Injectable({
    providedIn: 'root'
})
export class LineService {
    title = 'LineService';

    constructor(
        private alertModalService: AlertModalService,
        private featuresApplyInsertService: FeaturesApplyInsertService,
        private featuresApplyUpdateService: FeatureApplyUpdateService,
        private spatialSequenceService: SpatialSequenceService,
        private spatialUtilService: SpatialUtilService
    ) {}

    commitLineFeatureCreation(line: any) {
        console.debug(`${this.title} - commitLineFeatureCreation`);

        const sequence = this.spatialSequenceService.getLineSequence(1);
        if (sequence.length) {
            line.attributes.id = sequence[0];
        }

        const lineGraphic = new (window as any).esri.Graphic(line.geometry);
        lineGraphic.attributes = line.attributes;

        return this.featuresApplyInsertService.insertFeaturesByType(this.spatialUtilService.getLineMapLayer(), [lineGraphic]).pipe(
            tap(createdLines => {
                if (createdLines && createdLines.length > 0) {
                    console.debug('Line has been successfully created!');
                } else {
                    console.debug('Error creating line!');
                    this.alertModalService.createAlertMessage('Error occurred creating line feature.');
                }
            })
        );
    }

    create(feature: any) {
        console.debug(`${this.title} - create`);

        return this.commitLineFeatureCreation(feature);
    }

    update(feature: any) {
        console.debug(`${this.title} - update`);

        return this.featuresApplyUpdateService
            .updateFeaturesByType(this.spatialUtilService.getLineMapLayer(), [feature])
            .pipe(tap(() => console.debug('Line has been successfully updated!')));
    }

    calculateBearing(x1: number, y1: number, x2: number, y2: number) {
        console.debug(`${this.title} - calculateBearing`);

        const azimuth = Math.atan2(x2 - x1, y2 - y1);
        const azimuthInDegrees = (azimuth * 180) / Math.PI;

        let fixAzimuth = azimuthInDegrees % 360;
        if (fixAzimuth < 0) {
            fixAzimuth += 360;
        }

        return fixAzimuth;
    }

    getPointOffsetByBearingAndDistance(bearing: number, distance: number) {
        console.debug(`${this.title} - getPointOffsetByBearingAndDistance`);

        return {x: distance * Math.sin((bearing * Math.PI) / 180), y: distance * Math.cos((bearing * Math.PI) / 180)};
    }

    applyPolylineUpdates(polylines: any[]) {
        const logPrefix = `${this.title} - applyPolylineUpdates`;
        console.debug(logPrefix);

        this.spatialUtilService.applyEdits('line', null, polylines, null).subscribe({
            next: res => {
                console.debug(`${logPrefix} - updates:`, res.updates);

                if (res.updates && res.updates.every(u => u.success || u.error.code === 1011 /*Ignore 1011: Object is missing*/)) {
                    console.debug(`${logPrefix} - lines updated successfully`);
                } else if (res.updates) {
                    const errorMessage = res.updates[0].error?.message ? ' Error : ' + res.updates[0].error.message : '';
                    this.alertModalService.createAlertMessage('Error updating polyline features.' + errorMessage, 'Ok');
                }
            },
            error: err => this.alertModalService.createAlertMessage('Error updating polyline features. Error : ' + err, 'Ok')
        });
    }

    calcDistance(pointGeometry1: any, pointGeometry2: any) {
        let distance: number;

        if (
            (pointGeometry1.spatialReference && parseInt(pointGeometry1.spatialReference.wkid) === 7844) ||
            (pointGeometry2.spatialReference && parseInt(pointGeometry2.spatialReference.wkid) === 7844)
        ) {
            distance = this.spatialUtilService.geodesicDistance(pointGeometry1, pointGeometry2);
        } else {
            distance = Math.sqrt(Math.pow(pointGeometry2.x - pointGeometry1.x, 2) + Math.pow(pointGeometry2.y - pointGeometry1.y, 2));
        }

        return distance;
    }

    getLinesBetweenPoints(fromPoint: any, toPoint: any) {
        return this.spatialUtilService
            .getLines()
            .filter(
                (f: any) =>
                    (f.attributes.frompointid === fromPoint.attributes.id && f.attributes.topointid === toPoint.attributes.id) ||
                    (f.attributes.frompointid === toPoint.attributes.id && f.attributes.topointid === fromPoint.attributes.id)
            );
    }
}
